import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { pb } from '../../lib/pb'
import { money } from '../../lib/format'
import { getMetrics, type AdminMetrics } from '../../lib/adminApi'
import { planById } from '../../lib/constants'
import { Card, Skeleton, Badge } from '../../components/ui'
import { BarChart, LineChart, DistBars } from '../../components/admin/Charts'
import '../pages.css'
import './admin.css'

/** "2026-06" → "Jun". */
const monthLabel = (ym: string) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short' })

const PLAN_COLORS: Record<string, string> = {
  free: 'var(--c-slate)',
  solo: 'var(--c-accent)',
  team: 'var(--cred-vault)',
  scale: 'var(--cred-founding)',
  enterprise: 'var(--cred-live)',
  black: 'var(--c-text)',
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="admin-stat">
      <span className="admin-stat__label">{label}</span>
      <span className="admin-stat__value tnum">{value}</span>
      {sub && <span className="admin-stat__sub">{sub}</span>}
    </Card>
  )
}

export function AdminDashboard() {
  const [data, setData] = useState<AdminMetrics | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    getMetrics(pb.authStore.token)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Admin · Overview</h1>
          <p className="page__sub">
            Platform-wide reporting across every account.{' '}
            <Link to="/admin/subscribers" style={{ color: 'var(--c-pale)' }}>
              Subscribers
            </Link>{' '}
            ·{' '}
            <Link to="/admin/announcements" style={{ color: 'var(--c-pale)' }}>
              Announcements
            </Link>
          </p>
        </div>
      </div>

      {error ? (
        <Card>
          <p style={{ color: 'var(--c-error)' }}>Couldn’t load metrics: {error}</p>
        </Card>
      ) : !data ? (
        <div className="admin-stats">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="admin-stat">
              <Skeleton w="60%" h={12} />
              <Skeleton w="40%" h={26} />
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="admin-stats">
            <Stat
              label="MRR"
              value={money(data.summary.mrrCents)}
              sub={`${money(data.summary.arrCents)} ARR`}
            />
            <Stat
              label="Paying accounts"
              value={String(data.summary.paidAccounts)}
              sub={`${data.summary.activeSubscriptions} active subs`}
            />
            <Stat
              label="Total accounts"
              value={String(data.summary.totalAccounts)}
              sub={`${data.summary.recruitersCount} recruiters`}
            />
            <Stat
              label="Waitlist"
              value={data.summary.waitlistTotal.toLocaleString('en-US')}
              sub={`${data.summary.candidatesCount} candidates`}
            />
          </div>

          <div className="admin-grid">
            <Card>
              <div className="admin-card__head">
                <span className="admin-card__title">MRR over time</span>
                <span className="admin-card__note">last 12 months</span>
              </div>
              <LineChart
                points={data.mrrSeries.map((p) => ({ label: monthLabel(p.month), value: p.mrrCents }))}
                format={money}
              />
            </Card>

            <Card>
              <div className="admin-card__head">
                <span className="admin-card__title">New accounts</span>
                <span className="admin-card__note">signups / month</span>
              </div>
              <BarChart
                points={data.signupsSeries.map((p) => ({ label: monthLabel(p.month), value: p.count }))}
                color="var(--c-accent)"
              />
            </Card>

            <Card>
              <div className="admin-card__head">
                <span className="admin-card__title">Churn</span>
                <span className="admin-card__note">canceled / month</span>
              </div>
              <BarChart
                points={data.churnSeries.map((p) => ({
                  label: monthLabel(p.month),
                  value: p.canceled,
                  title: `${monthLabel(p.month)}: ${p.canceled} canceled · ${(p.rate * 100).toFixed(1)}% churn`,
                }))}
                color="var(--c-error)"
              />
            </Card>

            <Card>
              <div className="admin-card__head">
                <span className="admin-card__title">Plan mix</span>
                <span className="admin-card__note">accounts · MRR</span>
              </div>
              <DistBars
                rows={data.planDistribution.map((p) => ({
                  label: planById(p.planId)?.name ?? p.planId,
                  value: p.count,
                  sub: money(p.mrrCents),
                  color: PLAN_COLORS[p.planId],
                }))}
              />
            </Card>

            <Card>
              <div className="admin-card__head">
                <span className="admin-card__title">Activity</span>
                <span className="admin-card__note">actions / day · 14d</span>
              </div>
              <BarChart
                points={data.activitySeries.map((p) => ({
                  label: p.date.slice(8),
                  value: p.count,
                  title: `${p.date}: ${p.count}`,
                }))}
                color="var(--cred-vault)"
              />
            </Card>

            <Card>
              <div className="admin-card__head">
                <span className="admin-card__title">Recent subscription events</span>
              </div>
              {data.recentEvents.length === 0 ? (
                <p className="admin-card__note">No events yet.</p>
              ) : (
                <div className="ann-list">
                  {data.recentEvents.map((ev, i) => (
                    <div key={i} className="ann-row">
                      <div className="ann-row__main">
                        <div style={{ fontSize: '0.85rem', color: 'var(--c-text)' }}>
                          {ev.accountName || ev.accountId}
                        </div>
                        <div className="admin-table__sub">
                          {ev.kind.replace(/_/g, ' ')} → {planById(ev.planId)?.name ?? ev.planId}
                        </div>
                      </div>
                      <Badge>{money(ev.mrrCents)}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
